import * as z from "zod";
import { emailAddress } from "../email-address";
import { externalApps, sha256HexText, linkToken as token } from "../index";
import { mfaResetRequestDto } from "./mfa-reset";

// Authentication of the external users (ADR-024, ADR-147): the inputs and answers of the identity operations that
// the gateways of the external users call, and of the two that Backstage asks for an external user. The same schemas
// validate the routes of the gateways and the RPC methods, but for a `…Body`, which identity gets with the app of the
// gateway (`…Input`, ADR-206).

/**
 * How long the session of an external user lasts (ADR-147, NIST AAL2 and ASVS L2 V3.3): 30 minutes without a request,
 * 12 hours from the sign-in whatever happens. No "remember me" in F1. gateway-atelier applies it.
 */
export const EXTERNAL_SESSION = { idleSeconds: 30 * 60, absoluteSeconds: 12 * 60 * 60 } as const;

/** NIST SP 800-63B 5.1.1.2 (ADR-024): length only, no composition rules; counted in characters, not bytes. */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/**
 * A password as typed. The length is checked by the rules of identity, with the other problems, so the answer lists
 * them all at once; this bound only keeps a huge body away from the hash.
 */
const password = z.string().max(PASSWORD_MAX_LENGTH * 4);

const email = emailAddress;

/**
 * The app of the gateway that calls (ADR-206): where the link of a reset or of a change of the email leads, and whose
 * relying party a passkey ceremony is for. The gateway adds it to the body of the request, so a page never chooses.
 */
export const appInput = z.object({ app: z.enum(externalApps) });

/**
 * The pages of every app of the external users the links of identity's emails open (ADR-147, ADR-163, ADR-216):
 * identity writes the links, and @nexxt/account-ui mounts the pages there. The token is in the fragment, which no
 * server ever receives.
 */
export const authLinkPaths = {
  invitation: "/accept-invitation",
  passwordReset: "/reset-password",
  emailChange: "/confirm-email",
} as const;
export type AuthLinkPage = keyof typeof authLinkPaths;

export const loginWithPasswordInput = z.object({ email, password });
export type LoginWithPasswordInput = z.input<typeof loginWithPasswordInput>;

export const acceptInvitationInput = z.object({ token, password });
export type AcceptInvitationInput = z.input<typeof acceptInvitationInput>;

/** Body of `/auth/password/forgot`; identity gets it with the app of the gateway. */
export const requestPasswordResetBody = z.object({ email });
export type RequestPasswordResetBody = z.input<typeof requestPasswordResetBody>;
export const requestPasswordResetInput = requestPasswordResetBody.extend(appInput.shape);
export type RequestPasswordResetInput = z.input<typeof requestPasswordResetInput>;

export const resetPasswordInput = z.object({ token, password });
export type ResetPasswordInput = z.input<typeof resetPasswordInput>;

/**
 * The signed-in user changes its own password. Who it is comes from the Actor of the session, never from the input
 * (ADR-148): a gateway cannot name another user.
 */
export const changePasswordInput = z.object({ currentPassword: password, newPassword: password });
export type ChangePasswordInput = z.input<typeof changePasswordInput>;

/**
 * The signed-in user asks to change its own email (ASVS V3.7.1, ADR-163): the current password stands for the
 * re-authentication, as in a change of the password, so a session taken over is not enough to take the account. A link
 * goes to the new address, and the email changes only when it is opened. Who asks comes from the Actor of the session,
 * never from the input (ADR-148).
 */
/** Body of `/auth/email/change`; identity gets it with the app of the gateway. */
export const requestEmailChangeBody = z.object({ newEmail: email, currentPassword: password });
export type RequestEmailChangeBody = z.input<typeof requestEmailChangeBody>;
export const requestEmailChangeInput = requestEmailChangeBody.extend(appInput.shape);
export type RequestEmailChangeInput = z.input<typeof requestEmailChangeInput>;

/** The token of the link sent to the new address: opened with no session, as the reset of a password is (ADR-163). */
export const confirmEmailChangeInput = z.object({ token });
export type ConfirmEmailChangeInput = z.input<typeof confirmEmailChangeInput>;

/** "Sign out everywhere" of the signed-in user, named by the Actor of the session (ADR-148). */
export const logoutEverywhereInput = z.object({});
export type LogoutEverywhereInput = z.input<typeof logoutEverywhereInput>;

/**
 * A session of the user ended in a gateway, which names the user after deleting it: identity closes their live sockets
 * (ADR-310).
 */
export const closeLiveAccessInput = z.object({ userId: z.uuid() });
export type CloseLiveAccessInput = z.input<typeof closeLiveAccessInput>;

/** An administrator, on an external user of Backstage. */
export const externalUserInput = z.object({ userId: z.uuid() });
export type ExternalUserInput = z.input<typeof externalUserInput>;

/**
 * How an external user signs in, for the panel of the user in Backstage: whether it has a password, until when it is
 * locked, its second factors, its invitation, and the reset of the factors that waits for a second internal user
 * (ADR-201). Never a hash, a token or a count of failures.
 */
export const externalAccessDto = z
  .object({
    hasPassword: z.boolean(),
    /** Null when not locked, or when the lock has ended. */
    lockedUntil: z.iso.datetime().nullable(),
    /** The second factors, without what they are: whether a TOTP is confirmed, how many passkeys (ADR-151). */
    mfa: z.object({ totp: z.boolean(), passkeys: z.number().int() }),
    invitation: z
      .object({
        /** Null while the email has not left. */
        lastSentAt: z.iso.datetime().nullable(),
        expiresAt: z.iso.datetime(),
        reminderCount: z.number().int(),
        acceptedAt: z.iso.datetime().nullable(),
      })
      .nullable(),
    /** Null when no reset of the factors waits for an approval. */
    mfaReset: mfaResetRequestDto.nullable(),
  })
  .meta({ id: "ExternalAccess" });
export type ExternalAccessDto = z.infer<typeof externalAccessDto>;

export const unlockedDto = z.object({ unlocked: z.literal(true) });
export const invitationResentDto = z.object({ expiresAt: z.iso.datetime() });

// --- The routes /auth/* of gateway-atelier (ADR-148) ---

/**
 * Header with the Turnstile token of the page (ADR-024, point 5), on the sign-in, the request of a reset, the reset
 * and the acceptance of an invitation: a header, so the bodies stay the inputs of identity.
 */
export const TURNSTILE_HEADER = "x-nexxt-turnstile";

/**
 * The test secrets of Turnstile (Cloudflare documentation: always passes, always fails, token already spent): only
 * locally. Their answer names no page of ours, so the gateway does not compare the hostname, and `pnpm secrets:set`
 * refuses them for every environment (ADR-148, ADR-154).
 */
export const isTurnstileTestSecret = (secret: string | undefined) => /^[123]x0{31}AA$/.test(secret ?? "");

/**
 * A session was opened: the app then asks `/auth/me`. With `mfaEnrollmentRequired` a role of the user requires a
 * second factor it has not added yet: until it does, the session reaches only the routes that add one (ADR-151).
 */
export const signedInDto = z.object({ signedIn: z.literal(true), mfaEnrollmentRequired: z.boolean() });
/** Always the same answer, whether the email has an account or not (ADR-147, point 8). */
export const passwordResetRequestedDto = z.object({ requested: z.literal(true) });
export const passwordResetDto = z.object({ reset: z.literal(true) });
export const passwordChangedDto = z.object({ changed: z.literal(true) });
/** The link left, or would have if nothing stopped it: whether the address is taken is told only to its owner. */
export const emailChangeRequestedDto = z.object({ requested: z.literal(true) });
export const emailChangedDto = z.object({ changed: z.literal(true) });
export const signedOutDto = z.object({ signedOut: z.literal(true) });

/**
 * An open session of the signed-in user, in "my account" (ASVS V3.3.4, ADR-162). `id`: a handle that opens nothing,
 * not the cookie. Where it was opened from, as Cloudflare saw it; null for what the request did not say.
 */
export const sessionDto = z.object({
  id: sha256HexText,
  current: z.boolean(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable(),
  city: z.string().nullable(),
});
export type SessionDto = z.infer<typeof sessionDto>;
export const sessionsDto = z.object({ sessions: z.array(sessionDto) });
export const sessionClosedDto = z.object({ closed: z.literal(true) });

/** Codes of the gateways of the external users before identity is asked (ADR-148, ADR-206): each with a text in the app. */
export type ExternalAuthGatewayErrorCode =
  | "rate_limited"
  | "turnstile_failed"
  | "turnstile_unavailable"
  /** A role requires a second factor, and the session has none yet (ADR-151). */
  | "mfa_enrollment_required"
  /** A change to the factors needs the password typed again (`REAUTHENTICATION_SECONDS`). */
  | "reauthentication_required"
  /** Right credentials, but no role of the user opens the app of the gateway: no session (ADR-157). */
  | "app_not_allowed"
  /** No open session of the user with that id: closed already, expired, or someone else's (ADR-162). */
  | "session_not_found"
  /** The session of the request is closed by signing out, not from the list (ADR-162). */
  | "current_session";

/** Domain error codes of identity (ADR-046): thrown by the worker, translated by the apps. */
export type IdentityErrorCode =
  | "invalid_credentials"
  | "token_invalid"
  | "password_invalid"
  | "current_password_wrong"
  | "not_external_user"
  /** The new email is the current one. */
  | "email_change_invalid"
  /** Told once the link of the new address is opened, so only its owner learns that an account has it (ADR-163). */
  | "email_taken"
  | "invitation_not_pending"
  | "invitation_not_sent"
  /** The second step of a sign-in came too late, or after too many wrong codes: sign in again. */
  | "mfa_challenge_invalid"
  /** A wrong TOTP or recovery code: it counts towards the lockout, like a wrong password. */
  | "mfa_code_invalid"
  | "passkey_invalid"
  /** Passkeys need the address of the app, their relying party (ADR-151, ADR-206). */
  | "passkeys_unavailable"
  /** Without the keys of the TOTP secrets in the environment. */
  | "totp_unavailable"
  | "passkey_limit_reached"
  | "totp_already_enrolled"
  | "totp_not_pending"
  /** The last second factor of a user whose role requires one. */
  | "factor_required"
  /** Recovery codes come with a second factor; a reset asks for one to take away. */
  | "mfa_not_enrolled"
  /** The contact called back is not a contact of the user's organization with a phone (ADR-201). */
  | "callback_contact_invalid"
  /** A reset of the factors of the user already waits for an approval. */
  | "mfa_reset_pending"
  /** The request was approved or rejected already. */
  | "mfa_reset_not_pending"
  /** The gate `identity.mfaReset` is off or missing: no reset without four eyes. */
  | "mfa_reset_unavailable";

/** Rules of the `details.issues` of a refused password (`ValidationIssue.rule`). */
export type IdentityIssueRule = "too_short" | "too_long" | "contains_email" | "compromised" | "same_as_current";
